import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { ClientOnMap } from '@losan/core';

interface Props {
  clients: ClientOnMap[];
}

const eurFmt = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const dateFmt = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });

function statusLabel(status: ClientOnMap['status']): string {
  return status === 'green' ? 'Objetivo cumplido' : status === 'yellow' ? 'Bajo objetivo' : 'Sin compras este año';
}

export function ClientsMap({ clients }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  // Initialize the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Re-render pins whenever the client list changes.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    if (clients.length === 0) {
      map.setView([41.5, 2.4], 9);
      return;
    }

    const bounds = L.latLngBounds([]);
    for (const c of clients) {
      const icon = L.divIcon({
        className: 'map-pin-wrapper',
        html: `<span class="map-pin map-pin-${c.status}" aria-label="${statusLabel(c.status)}"></span>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -9],
      });
      const popup = `
        <div class="map-popup">
          <strong>${escapeHtml(c.commercialName)}</strong>
          <div class="map-popup-row"><span>Estado</span><span class="map-popup-status map-popup-status-${c.status}">${statusLabel(c.status)}</span></div>
          <div class="map-popup-row"><span>Facturación YTD</span><span>${eurFmt.format(c.revenueYTD)}</span></div>
          <div class="map-popup-row"><span>Último pedido</span><span>${c.lastOrderDate ? dateFmt.format(new Date(c.lastOrderDate)) : '—'}</span></div>
        </div>
      `;
      const marker = L.marker([c.location.lat, c.location.lng], { icon }).bindPopup(popup);
      marker.addTo(layer);
      bounds.extend([c.location.lat, c.location.lng]);
    }
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 12 });
  }, [clients]);

  return <div ref={containerRef} className="clients-map" role="region" aria-label="Mapa de clientes" />;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch === '"' ? '&quot;' : '&#39;',
  );
}
